/* Iter 228 — shared "Use invoice" dialog for the four document tools
 * (Bulk Data / Packing List / Pack Team Print / Bulk Label).
 * Asks the two things that used to be implicit: sender company (essential
 * before any output) and whether to attach a packing slip now or generate
 * without one. The choice feeds the per-flow "used ×N" badge.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { Building2, FileUp, Zap, X } from "lucide-react";
import { Button } from "./ui/button";
import { API, getAuthHeader } from "@/App";

const TOOL_LABELS = {
  bulk_data: "Bulk Data",
  packing_list: "Packing List",
  pack_team: "Pack Team Print",
  bulk_label: "Bulk Label",
};
// bulk_label consumes an optional Packing LIST (product names), the other
// tools consume a Packing SLIP (batch data).
const FILE_LABELS = {
  bulk_data: "Packing Slip",
  packing_list: "Packing Slip",
  pack_team: "Packing Slip",
  bulk_label: "Packing List (optional product names)",
};

export const UseInvoiceDialog = ({ open, invoice, tool, companies,
                                    defaultCompany, busy, onClose,
                                    onConfirm }) => {
  const [company, setCompany] = useState("");
  const [attachFile, setAttachFile] = useState(null);
  const [fetchedCompanies, setFetchedCompanies] = useState([]);

  useEffect(() => {
    if (open) {
      setCompany(defaultCompany || "");
      setAttachFile(null);
    }
  }, [open, defaultCompany]);

  // Self-fetch the company master list when the host panel hasn't loaded
  // it (e.g. Pack Team Print has no company select of its own).
  useEffect(() => {
    if (!open) return;
    if (companies && companies.length) return;
    let cancelled = false;
    axios.get(`${API}/companies`, { headers: getAuthHeader() })
      .then(r => { if (!cancelled) setFetchedCompanies(r.data || []); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, companies]);

  if (!open || !invoice) return null;
  const companyList = (companies && companies.length) ? companies : fetchedCompanies;
  const toolLabel = TOOL_LABELS[tool] || tool;
  const fileLabel = FILE_LABELS[tool] || "Packing Slip";
  const companyOk = !!company;

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4"
         onClick={onClose} data-testid="use-invoice-dialog">
      <div className="bg-white rounded-xl shadow-2xl border border-slate-200 p-5 w-[440px] max-w-[95vw]"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-base font-semibold text-slate-900">
            Generate {toolLabel}
          </h3>
          <button onClick={onClose}
                  className="text-slate-400 hover:text-slate-600"
                  data-testid="use-invoice-close">
            <X className="w-4 h-4" />
          </button>
        </div>
        <p className="text-xs text-slate-500 mb-4">
          Invoice <span className="font-mono font-bold text-emerald-700"
                        data-testid="use-invoice-no">{invoice.invoice_no}</span>
          {" "}· {invoice.parcel_count} parcel{invoice.parcel_count === 1 ? "" : "s"}
        </p>

        <label className="block text-xs font-semibold text-slate-700 mb-1">
          Sender Company <span className="text-red-500">*</span>
        </label>
        <div className="relative mb-4">
          <Building2 className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <select value={company} onChange={(e) => setCompany(e.target.value)}
                  className="w-full h-8 pl-7 pr-2 rounded border border-slate-300 text-xs bg-white"
                  data-testid="use-invoice-company">
            <option value="">Select sender company…</option>
            {(companyList || []).map(c => (
              <option key={c.id || c.name} value={c.id || c.name}>
                {c.name}
              </option>
            ))}
          </select>
        </div>

        <label className="block text-xs font-semibold text-slate-700 mb-1">
          {fileLabel} <span className="text-slate-400 font-normal">(optional — adds batch/expiry data)</span>
        </label>
        <input type="file"
               accept={tool === "bulk_label" ? ".xlsx,.xls" : ".pdf,.xlsx,.xls"}
               onChange={(e) => setAttachFile(e.target.files?.[0] || null)}
               className="w-full text-xs border border-slate-300 rounded-lg p-1.5 mb-4"
               data-testid="use-invoice-slip-file" />

        <div className="flex flex-col gap-2">
          <Button disabled={!companyOk || busy || !attachFile}
                  onClick={() => onConfirm({ slipFile: attachFile, companyId: company })}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white h-8 text-xs"
                  title={attachFile ? `Generate with ${fileLabel}` : `Pick a ${fileLabel} file first`}
                  data-testid="use-invoice-with-slip">
            <FileUp className="w-3.5 h-3.5 mr-1" />
            Generate with {tool === "bulk_label" ? "Packing List" : "Packing Slip"}
          </Button>
          <Button disabled={!companyOk || busy} variant="outline"
                  onClick={() => onConfirm({ slipFile: null, companyId: company })}
                  className="h-8 text-xs border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                  data-testid="use-invoice-no-slip">
            <Zap className="w-3.5 h-3.5 mr-1" />
            Generate without {tool === "bulk_label" ? "Packing List" : "Packing Slip"}
          </Button>
          {!companyOk && (
            <p className="text-[10px] text-amber-600 text-center">
              Sender company is required before generating.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default UseInvoiceDialog;
